import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {I18NEXT_SERVICE, ITranslationService} from 'angular-i18next';
import {CurrentUser} from 'app/core/services/auth.service';
import {MixpanelService} from 'app/core/services/mixpanel.service';
import {IntercomService} from 'app/core/services/intercom.service';
import {QueryParamsHandling, Router} from '@angular/router';
import {uaSettings_analytics_uaSettings} from 'app/features/analytics/service/gql/uaSettings';
import {AppInfo, SelectedAppService} from 'app/core/services/current-app/selected-app.service';
import {trackById} from 'app/core/helpers/trackby';
import {NavService} from 'app/core/services/nav.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';

export interface NavItem {
    id: string;
    link: string | null;
    useRouterLink?: boolean;
    iconClass: string;
    label: string;
    target?: string;
    secure?: boolean;
    visible: boolean;
    exactMatch?: boolean;
    openIntercom?: boolean;
    children?: NavItem[];
    trackEvent?: string;
    sup?: string;
    queryParams?: QueryParamsHandling | null;
    reloadRoute?: boolean;
}

@Component({
    selector: 'navigation-side-bar',
    templateUrl: './navigation-side-bar.component.html',
    styleUrls: ['./navigation-side-bar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationSideBarComponent implements OnChanges, OnInit, OnDestroy {
    @Input() currentUser: CurrentUser;
    @Input() currentApp: AppInfo | null;
    @Input() faqUrl: string;
    @Input() uaSettings: uaSettings_analytics_uaSettings;
    @Input() navExpanded: boolean;
    @Input() nav: HTMLElement;
    @ViewChild('navItemWithChild', {static: false}) navItemWithChild: ElementRef<HTMLElement>;

    expandedItem: NavItem | null;
    navItems: NavItem[];
    animatedContentHeight: number;

    protected readonly trackById = trackById;

    get selectedAppInfo() {
        return this.currentAppService.selectedAppInfo;
    }

    destroy$ = new Subject<void>();

    constructor(
        private mixpanel: MixpanelService,
        private intercom: IntercomService,
        @Inject(I18NEXT_SERVICE) private i18Next: ITranslationService,
        private router: Router,
        public cd: ChangeDetectorRef,
        public currentAppService: SelectedAppService,
        public navService: NavService
    ) {}

    private trackClick(item: NavItem) {
        if (!item.trackEvent) {
            return;
        }

        this.mixpanel.track(item.trackEvent);
    }

    ngOnChanges(changes: SimpleChanges) {
        this.navService.buildNavMenu(this.navExpanded);

        if (changes.navExpanded && changes.navExpanded.currentValue !== changes.navExpanded.previousValue) {
            this.closeExpandedMenu();
        }
    }

    ngOnInit() {
        if (!this.navExpanded) {
            this.expandedItem = null;
        }
        this.navService.init(this.navExpanded);

        this.navService.navItems$.pipe(takeUntil(this.destroy$)).subscribe(navItems => (this.navItems = navItems));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onClickItem(item: NavItem, event: MouseEvent) {
        if (item.reloadRoute) {
            this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
                this.router.navigate([item.link]);
            });

            this.preventEvents(event);
            return;
        }

        if (item.id === 'campaignManagement') {
            this.router.navigate([item.link], {
                queryParams: {
                    // eslint-disable-next-line max-len
                    q: '(f~(DATE~(relativeDate~last7Days)USER~!~APP~!~COUNTRY~!~IS*_ORGANIC~!~ATTRIBUTION*_NETWORK*_HID~!)g~!ATTRIBUTION*_NETWORK*_HID~ATTRIBUTION*_CAMPAIGN*_HID~~m~!installs~spend~(id~retention*_rate~d~1)(id~retention*_rate~d~3)(id~retention*_rate~d~7)(id~roas*_forecast~d~1)(id~roas*_forecast~d~3)(id~roas*_forecast~d~7)(id~roas*_forecast~d~30)(id~roas*_forecast~d~180)~view~table~fv~*~topLimit~7)',
                },
            });

            this.preventEvents(event);
            return;
        }

        if (item.openIntercom && this.intercom.intercomClient) {
            this.intercom.intercomClient('showMessages');

            this.preventEvents(event);
            return;
        }

        if (item.useRouterLink && !event.metaKey && !event.ctrlKey) {
            if (!this.navExpanded) {
                event.preventDefault();
                this.expandedItem = null;
                this.router.navigate([item.link], {queryParamsHandling: item.queryParams});
                return;
            }

            return event.preventDefault();
        }

        event.stopPropagation();
        event.stopImmediatePropagation();

        this.trackClick(item);
    }

    onClickItemWidthChild(item: NavItem, event: MouseEvent, navChild: HTMLElement) {
        this.preventEvents(event);
        this.expandedItem = this.expandedItem === item ? null : item;
        this.trackClick(item);

        if (!this.navExpanded) {
            setTimeout(() => this.setPositionSubNav(navChild), 0);
        } else {
            const animateContent = navChild.parentElement.nextElementSibling;
            animateContent.removeAttribute('style');
        }
    }

    preventEvents(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
    }

    setPositionSubNav(navChild: HTMLElement) {
        const animateContent = navChild.parentElement.nextElementSibling;
        const rect = navChild.getBoundingClientRect();

        if (document.body.offsetHeight < rect.y + this.animatedContentHeight) {
            this.buildSubNavStyles(animateContent, rect, this.animatedContentHeight);
            return;
        }

        this.buildSubNavStyles(animateContent, rect, 0);
    }

    buildSubNavStyles(animateContent, rect, height) {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        let concatStyles = animateContent.getAttribute('style') + `position: fixed; top: ${rect.y - height}px; left: ${rect.x + 80}px`;
        animateContent.setAttribute('style', concatStyles);
    }

    closeExpandedMenu() {
        this.expandedItem = null;
    }
}
